// "Race to the cut-off" — the fun bar above Orders out today (per Alex,
// 2026-09-09). ONE track on the ITEMS basis — items shipped of items due —
// while the ORDERS figure is the prominent one in the text (an order is not
// out until all its items are, so the two cannot finish apart).
//
// PACE (Alex, 2026-09-09, after one quiet quarter hour flipped the board to
// Danger zone while the day was on course): the BE sends packing history as
// 15-minute slices over the last 90 minutes; the pace is their WEIGHTED
// average with the newest slice counting most and each older slice half as
// much again every 45 minutes (HALF_LIFE_MIN). Slices before today's first
// pack carry no weight, so the first hour is not diluted by an empty night.
//
// An emoji runner sits at the fill edge facing the finish flag; the projected
// finish sits at the far right, and a status line under the title grades it
// against the 15:30 dispatch cut-off:
//   Flying 🔥 (bright green)  — finish ≥ FLYING_MARGIN_MIN before cut-off
//   On track 😎 (green)       — finish before cut-off
//   Behind 😮 (amber)         — finish up to DANGER_AFTER_MIN past cut-off
//   Danger zone ⚠️ (red)      — later than that (or not today)
// The status word only changes once the new band has held for
// BAND_HOLD_POLLS consecutive payloads (hysteresis), so it does not flicker
// at the boundaries. After the cut-off the race freezes, like the orders card.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../i18n";
import { TeamRace, TeamRaceBucket, EMPTY_TEAM } from "../api/dashboardActivity";

const fmtInt = (n: number) => n.toLocaleString();

export type RaceBand = "done" | "flying" | "on_track" | "behind" | "danger" | "idle" | "frozen";

export interface Pace {
    /** Items per millisecond; null when there is not enough history. */
    perMs: number | null;
    /** Weighted items/hr for display (0 when null). */
    itemsPerHour: number;
    /** Items actually packed across the weighted history. */
    itemsInHistory: number;
}

export interface LaneState {
    pct: number;            // 0..100, floored so 100 means truly finished
    remaining: number;
    etaMs: number | null;   // null = no estimate (done, idle, or frozen)
    band: RaceBand;
}

// Minimum items packed across the history before we trust a pace.
export const MIN_HISTORY_ITEMS = 10;
// Weight halves every this many minutes of age.
export const HALF_LIFE_MIN = 45;
// Finish this many minutes (or more) before the cut-off = "Flying"
// (60 → 30 on 2026-09-09, Alex).
export const FLYING_MARGIN_MIN = 30;
// Finish later than this many minutes after the cut-off = "Danger zone".
export const DANGER_AFTER_MIN = 30;
// A new status band must hold for this many payloads before it shows.
export const BAND_HOLD_POLLS = 2;

// Pure: weighted pace over the slices. Each slice k covers
// [now − (k+1)·w, now − k·w]; its weight is 0.5^(k·w / HALF_LIFE) times the
// fraction of the slice that falls after the day's first pack.
export const weightedPace = (
    buckets: TeamRaceBucket[], windowMinutes: number, firstPackMs: number | null, nowMs: number,
): Pace => {
    const w = windowMinutes * 60_000;
    if (w <= 0 || buckets.length === 0) return { perMs: null, itemsPerHour: 0, itemsInHistory: 0 };
    let weightedItems = 0;
    let weightedMs = 0;
    let itemsInHistory = 0;
    for (const b of buckets) {
        const sliceStart = nowMs - (b.age + 1) * w;
        const sliceEnd = nowMs - b.age * w;
        const coveredMs = firstPackMs === null
            ? w
            : Math.max(0, sliceEnd - Math.max(sliceStart, firstPackMs));
        if (coveredMs <= 0) continue;
        const weight = Math.pow(0.5, (b.age * windowMinutes) / HALF_LIFE_MIN);
        weightedItems += weight * b.items;
        weightedMs += weight * coveredMs;
        itemsInHistory += b.items;
    }
    if (weightedMs <= 0 || itemsInHistory < MIN_HISTORY_ITEMS) {
        return { perMs: null, itemsPerHour: 0, itemsInHistory };
    }
    const perMs = weightedItems / weightedMs;
    return { perMs, itemsPerHour: Math.round(perMs * 3_600_000), itemsInHistory };
};

// Pure: works out the runner's position, estimate and status band.
export const laneState = (
    done: number, due: number, pace: Pace, nowMs: number, cutoffMs: number | null,
): LaneState => {
    const remaining = Math.max(0, due - done);
    const pct = due > 0 ? Math.min(100, Math.floor((done / due) * 100)) : 0;
    const finished = due > 0 && remaining === 0;
    if (finished) return { pct: 100, remaining: 0, etaMs: null, band: "done" };
    if (cutoffMs !== null && nowMs >= cutoffMs) return { pct, remaining, etaMs: null, band: "frozen" };
    if (pace.perMs === null || pace.perMs <= 0 || due === 0) {
        return { pct, remaining, etaMs: null, band: "idle" };
    }
    const etaMs = nowMs + remaining / pace.perMs;
    let band: RaceBand = "on_track";
    if (cutoffMs !== null) {
        if (etaMs > cutoffMs + DANGER_AFTER_MIN * 60_000) band = "danger";
        else if (etaMs > cutoffMs) band = "behind";
        else if (etaMs <= cutoffMs - FLYING_MARGIN_MIN * 60_000) band = "flying";
    }
    return { pct, remaining, etaMs, band };
};

// The time lorry 🚚: where the clock is between the day start (06:00) and
// the cut-off (15:30), 0..100 — the same speed every day, so the runner is
// racing it (Alex, 2026-09-09). Null when either instant is unknown.
export const clockPct = (startMs: number | null, endMs: number | null, nowMs: number): number | null => {
    if (startMs === null || endMs === null || endMs <= startMs) return null;
    const pct = ((nowMs - startMs) / (endMs - startMs)) * 100;
    return Math.max(0, Math.min(100, pct));
};

// Convenience for tests and callers holding a raw payload.
export const raceState = (race: TeamRace, nowMs: number): LaneState => {
    const cutoffMs = race.cutoff_at ? Date.parse(race.cutoff_at) : null;
    const firstPackMs = race.first_pack_at ? Date.parse(race.first_pack_at) : null;
    const pace = weightedPace(race.buckets ?? [], race.window_minutes, firstPackMs, nowMs);
    return laneState(race.items_done, race.items_due, pace, nowMs, cutoffMs);
};

export const bandColour = (band: RaceBand): string => {
    switch (band) {
        case "flying": return "#22c55e";
        case "done":
        case "on_track": return "#0a8f3c";
        case "behind": return "#f59e0b";
        case "danger": return "#ef4444";
        default: return "#94a3b8";
    }
};

// Pace bands are debounced; the deterministic states commit at once.
const PACE_BANDS: ReadonlySet<RaceBand> = new Set(["flying", "on_track", "behind", "danger"]);

const STATUS: Partial<Record<RaceBand, { key: string; emoji: string }>> = {
    flying: { key: "dashboard_team_race_status_flying", emoji: "🔥" },
    on_track: { key: "dashboard_team_race_status_on_track", emoji: "😎" },
    behind: { key: "dashboard_team_race_status_behind", emoji: "😮" },
    danger: { key: "dashboard_team_race_status_danger", emoji: "⚠️" },
    done: { key: "dashboard_team_race_finished", emoji: "🏆" },
    frozen: { key: "dashboard_team_race_frozen", emoji: "🏁" },
    idle: { key: "dashboard_team_race_no_estimate", emoji: "⏳" },
};

const runnerEmoji = (band: RaceBand) => {
    if (band === "done") return "🏆";
    if (band === "idle" || band === "frozen") return "🧍";
    return "🏃";
};

const londonClock = (ms: number) =>
    new Date(ms).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });
const londonDate = (ms: number) =>
    new Date(ms).toLocaleDateString("en-CA", { timeZone: "Europe/London" });

// A projection that lands on a LATER day must not be shown as a bare
// clock time — "13:49" read as an early finish while the band said Danger
// zone (2026-09-09, a lull projected a finish the next day at 12:21).
export const sameLondonDay = (aMs: number, bMs: number) => londonDate(aMs) === londonDate(bMs);

interface Props {
    /** Undefined when the payload came from an older BE or a stored snapshot without the block. */
    race: TeamRace | undefined;
    nowMs?: number;
}

const DashboardRace = ({ race: raceIn, nowMs = Date.now() }: Props) => {
    const { t } = useTranslation();
    // Older payloads (pre-2026-09-09 BE, or a session-storage snapshot saved
    // by an older FE) have no race block — render the empty race, never crash.
    const race: TeamRace = raceIn ?? EMPTY_TEAM.race;
    const cutoffMs = race.cutoff_at ? Date.parse(race.cutoff_at) : null;
    const firstPackMs = race.first_pack_at ? Date.parse(race.first_pack_at) : null;
    const pace = weightedPace(race.buckets ?? [], race.window_minutes, firstPackMs, nowMs);
    const s = laneState(race.items_done, race.items_due, pace, nowMs, cutoffMs);

    // Hysteresis on the status word: a pace band has to be computed on
    // BAND_HOLD_POLLS consecutive payloads before it replaces the shown one.
    const [shownBand, setShownBand] = useState<RaceBand>(s.band);
    const pending = useRef<{ band: RaceBand; count: number } | null>(null);
    const lastPayload = useRef<string | null>(null);
    useEffect(() => {
        if (race.generated_at === lastPayload.current) return; // same payload re-rendered
        lastPayload.current = race.generated_at;
        if (!PACE_BANDS.has(s.band) || !PACE_BANDS.has(shownBand)) {
            pending.current = null;
            setShownBand(s.band);
            return;
        }
        if (s.band === shownBand) { pending.current = null; return; }
        const p = pending.current;
        const count = p && p.band === s.band ? p.count + 1 : 1;
        if (count >= BAND_HOLD_POLLS) {
            pending.current = null;
            setShownBand(s.band);
        } else {
            pending.current = { band: s.band, count };
        }
    }, [race.generated_at, s.band, shownBand]);

    const band = shownBand;
    const colour = bandColour(band);
    const running = s.etaMs !== null;
    const status = STATUS[band];
    const dayStartMs = race.day_start_at ? Date.parse(race.day_start_at) : null;
    const lorryPct = clockPct(dayStartMs, cutoffMs, nowMs);

    return (
        <section className={`tdash-race-card tdash-race-card--${band}`}>
            <div className="tdash-race-main">
                <div className="tdash-race-head">
                    <span className="tdash-label">{t("dashboard_team_race_title")}</span>
                    <span className="tdash-caption">
                        {t("dashboard_team_dispatch_cutoff", { time: race.dispatch_cutoff })}
                    </span>
                </div>
                {status && (
                    <div className="tdash-race-status" style={{ color: colour }}>
                        <span className="tdash-race-status-text">{t(status.key)}</span>
                        <span className="tdash-race-status-emoji" role="img" aria-hidden="true">{status.emoji}</span>
                    </div>
                )}
                <div className="tdash-race-track-wrap">
                    <div className="tdash-race-track">
                        <div className="tdash-race-fill" style={{ width: `${s.pct}%`, backgroundColor: colour }} />
                        {/* The runner emoji faces LEFT in most fonts; flip it
                            (CSS) so it runs towards the flag. */}
                        <span
                            className={`tdash-race-runner${running ? " tdash-race-runner--running" : ""}`}
                            style={{ left: `${s.pct}%` }}
                            role="img"
                            aria-label={`${s.pct}%`}
                        >
                            {runnerEmoji(band)}
                        </span>
                        {/* The clock: a lorry driving the track at constant
                            speed from 06:00 to the 15:30 cut-off. */}
                        {lorryPct !== null && (
                            <span
                                className="tdash-race-lorry"
                                style={{ left: `${lorryPct}%` }}
                                role="img"
                                aria-label={`${t("dashboard_team_race_clock")} ${Math.round(lorryPct)}%`}
                                title={t("dashboard_team_race_clock")}
                            >
                                🚚
                            </span>
                        )}
                    </div>
                    <span className="tdash-race-finish" role="img" aria-label="finish">🏁</span>
                </div>
                <div className="tdash-race-meta">
                    <span className="tdash-race-count">
                        {t("dashboard_team_race_orders_count", { done: fmtInt(race.orders_done), due: fmtInt(race.orders_due) })}
                    </span>
                    <span className="tdash-race-items">
                        {t("dashboard_team_race_items_count", { done: fmtInt(race.items_done), due: fmtInt(race.items_due) })}
                    </span>
                    <span className="tdash-race-pct" style={{ color: colour }}>{s.pct}%</span>
                </div>
            </div>
            <div className="tdash-race-projected">
                {/* Once the race is over (everything shipped, or the cut-off
                    has passed) the block shows the ACTUAL finish — the last
                    pre-cut-off pack of a due order — or stays blank when the
                    day was not finished (Alex, 2026-09-09). */}
                <span className="tdash-label">
                    {s.band === "done" || s.band === "frozen"
                        ? t("dashboard_team_race_finish_time")
                        : t("dashboard_team_race_projected")}
                </span>
                <span
                    className={`tdash-race-projected-time${s.etaMs !== null && !sameLondonDay(s.etaMs, nowMs) ? " tdash-race-projected-time--text" : ""}`}
                    style={{ color: colour }}
                >
                    {s.band === "done" || s.band === "frozen"
                        ? (s.band === "done" && race.last_pack_at ? londonClock(Date.parse(race.last_pack_at)) : "—")
                        : s.etaMs === null
                            ? "—"
                            : sameLondonDay(s.etaMs, nowMs)
                                ? londonClock(s.etaMs)
                                : t("dashboard_team_race_not_today")}
                </span>
                {s.etaMs === null && status && (
                    <span className="tdash-caption">{t(status.key)}</span>
                )}
                {/* The (smoothed) pace behind the projection, per hour. */}
                <span className="tdash-caption tdash-race-rate">
                    {t("dashboard_team_race_rate", { n: fmtInt(pace.itemsPerHour) })}
                </span>
            </div>
        </section>
    );
};

export default DashboardRace;
